import {
  executeImportPlan,
  translateImported,
  type ImportPlan,
  type ImportProgress,
  type ImportReport,
} from './referentialImportService';
import { logger } from './logger';

/**
 * Exécution de l'import hors du cycle de vie des composants : l'admin peut
 * refermer l'assistant, naviguer dans le backoffice et revenir, le traitement
 * continue. Un rechargement complet de la page l'interrompt en revanche, d'où
 * la garde `beforeunload` posée pendant le traitement.
 */

export type RunnerStatus = 'idle' | 'running' | 'translating' | 'done' | 'cancelled' | 'failed';

export interface RunnerState {
  status: RunnerStatus;
  progress: ImportProgress | null;
  report: ImportReport | null;
  error: string | null;
  startedAt: number | null;
}

const IDLE_STATE: RunnerState = {
  status: 'idle',
  progress: null,
  report: null,
  error: null,
  startedAt: null,
};

let state: RunnerState = IDLE_STATE;
let cancelRequested = false;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function publish(patch: Partial<RunnerState>) {
  state = { ...state, ...patch };
  notify();
}

function warnBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = '';
}

function isBusy(status: RunnerStatus) {
  return status === 'running' || status === 'translating';
}

export function subscribeImportRunner(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getImportRunnerState(): RunnerState {
  return state;
}

export function cancelImport() {
  if (isBusy(state.status)) cancelRequested = true;
}

export function resetImportRunner() {
  if (isBusy(state.status)) return;
  cancelRequested = false;
  state = IDLE_STATE;
  notify();
}

export async function startImport(plan: ImportPlan, translate: boolean): Promise<void> {
  if (isBusy(state.status)) return;

  cancelRequested = false;
  state = { status: 'running', progress: null, report: null, error: null, startedAt: Date.now() };
  notify();
  window.addEventListener('beforeunload', warnBeforeUnload);

  try {
    const { report, createdIds } = await executeImportPlan(
      plan,
      (progress) => publish({ progress }),
      () => cancelRequested,
    );

    let finalReport = report;
    const worthTranslating = translate && !cancelRequested && !report.abortedReason;

    if (worthTranslating) {
      publish({ status: 'translating', report });
      const queued = await translateImported(
        createdIds,
        (progress) => publish({ progress }),
        () => cancelRequested,
      );
      finalReport = { ...report, translationsQueued: queued };
    }

    publish({
      status: cancelRequested ? 'cancelled' : 'done',
      report: finalReport,
      progress: null,
    });
  } catch (err) {
    logger.error('import_runner', 'referentialImportRunner', { error: String(err) });
    publish({
      status: 'failed',
      error: err instanceof Error ? err.message : String(err),
      progress: null,
    });
  } finally {
    window.removeEventListener('beforeunload', warnBeforeUnload);
  }
}
